<script lang="ts">
  import Chart from '../charts/Chart.svelte';
  import Readout from '../charts/Readout.svelte';
  import { clockTime, length } from '../../utils/activities';
  import { units, usesMiles } from '../../stores/units.svelte';
  import { DURATION_STEPS, durationLabel, linearTicks, niceTicks, polyline, runsBy, segments, type Scale } from '../../utils/chart';
  import { hrZone, type Series, type SeriesPoint, type Zones } from '../../utils/training';
  import { decimal, t } from '../../i18n/index.ts';

  // ActivityCharts is an activity's pace or speed, heart rate, and elevation
  // over its distance, or a timer-only activity's heart rate over time
  // (spec 006 FR-041, 006 FR-042, 006 FR-043), with the time in each heart rate zone
  // under them (006 FR-054), in the shared chart language (spec 010 FR-033):
  // each chart has its own scale, the distance is marked each kilometer or
  // mile, and the heart rate line takes each stretch's zone color. Pauses
  // and gaps break the lines. Hovering or dragging over any chart puts one
  // guide across all of them, fills the readout above them, and hands the
  // point to onscrub for the map's marker; arrow keys move it too, and Page
  // Up and Page Down step a kilometer or mile at a time, reading that
  // split's averages (006 FR-043).
  interface Props {
    series: Series;
    zones?: Zones;
    onscrub: (p: SeriesPoint | null) => void;
  }
  let { series, zones, onscrub }: Props = $props();

  const ZONE_COLORS = ['var(--theme-sky)', 'var(--theme-green)', 'var(--theme-yellow)', 'var(--theme-peach)', 'var(--theme-red)'];
  // BELOW_ZONES colors heart rate under 50% of the maximum, which no zone counts.
  const BELOW_ZONES = 'var(--theme-overlay1)';
  let miles = $derived(usesMiles());
  let feet = $derived(units().elevation === 'ft');
  let unit = $derived(miles ? 1609.344 : 1000);

  interface Plot {
    id: string;
    // kind picks the chart's sentences in the catalog.
    kind: 'pace' | 'speed' | 'hr' | 'ele';
    title: string;
    color: string;
    unit: string;
    // pick is the charted number at a point in the viewer's unit.
    pick: (p: SeriesPoint) => number | undefined;
    format: (v: number) => string;
    tick: (v: number) => string;
    // invert puts the lowest value at the top: pace is fastest at its lowest.
    invert: boolean;
  }

  let byTime = $derived(series.by === 'time');
  let pts = $derived(series.runs.flat());
  const xOf = (p: SeriesPoint) => (byTime ? p.t : p.d);
  let xMax = $derived(pts.length ? Math.max(1, xOf(pts[pts.length - 1])) : 1);
  let positions = $derived(pts.map((p) => xOf(p) / xMax));

  const round = (v: number) => String(Math.round(v));
  const hr: Plot = { id: 'hr', kind: 'hr', title: t('activitiesHeartRate'), color: 'var(--theme-red)', unit: t('activitiesUnitBpm'), pick: (p) => p.hr, format: (v) => t('activitiesBpm', { n: Math.round(v) }), tick: round, invert: false };

  let charts = $derived.by(() => {
    const out: Plot[] = [];
    const has = (f: (p: SeriesPoint) => unknown) => pts.some((p) => f(p) != null);
    if (!byTime && has((p) => p.v)) {
      out.push(series.pace
        ? { id: 'v', kind: 'pace', title: t('activitiesChartPace'), color: 'var(--theme-blue)', unit: miles ? t('activitiesUnitMinPerMi') : t('activitiesUnitMinPerKm'), pick: (p) => (p.v != null ? (p.v * unit) / 1000 : undefined), format: (v) => (miles ? t('activitiesPacePerMi', { pace: clockTime(v) }) : t('activitiesPacePerKm', { pace: clockTime(v) })), tick: clockTime, invert: true }
        : { id: 'v', kind: 'speed', title: t('activitiesChartSpeed'), color: 'var(--theme-blue)', unit: miles ? t('activitiesUnitMph') : t('activitiesUnitKmh'), pick: (p) => (p.v != null ? p.v * (miles ? 2.23694 : 3.6) : undefined), format: (v) => (miles ? t('speedMph', { speed: decimal(v, 1) }) : t('speedKmh', { speed: decimal(v, 1) })), tick: round, invert: false });
    }
    if (has((p) => p.hr)) out.push(hr);
    if (!byTime && has((p) => p.ele)) {
      out.push({ id: 'ele', kind: 'ele', title: t('activitiesChartElevation'), color: 'var(--theme-green)', unit: feet ? t('activitiesUnitFt') : t('activitiesUnitM'), pick: (p) => (p.ele != null ? (feet ? p.ele * 3.28084 : p.ele) : undefined), format: (v) => (feet ? t('activitiesElevationFt', { n: Math.round(v) }) : t('activitiesElevationM', { n: Math.round(v) })), tick: round, invert: false });
    }
    return out;
  });

  interface Drawn {
    chart: Plot;
    scale: Scale;
    // lo and hi are the frame's ends: pace's are swapped, fastest at the top.
    lo: number;
    hi: number;
    lines: { color: string; points: string }[];
    avg: number;
    min: number;
    max: number;
  }

  const zoneColor = (v: number) => {
    const z = zones ? hrZone(v, zones.max_hr) : 0;
    return z < 0 ? BELOW_ZONES : ZONE_COLORS[z];
  };

  // drawn scales each chart to its values. A pace's slowest stretches,
  // such as a stop at a crossing, are cut off at 1.15 times the 95th
  // percentile so they do not flatten the rest of the line.
  let drawn = $derived(charts.map((c): Drawn => {
    const vals = pts.map(c.pick).filter((v): v is number => v != null);
    const sorted = [...vals].sort((a, b) => a - b);
    const min = sorted[0] ?? 0, max = sorted[sorted.length - 1] ?? 1;
    const cut = c.invert ? Math.min(max, sorted[Math.floor(0.95 * (sorted.length - 1))] * 1.15) : max;
    const scale = niceTicks(min, cut, c.invert ? { steps: DURATION_STEPS } : {});
    const lo = c.invert ? scale.hi : scale.lo, hi = c.invert ? scale.lo : scale.hi;
    const lines: { color: string; points: string }[] = [];
    for (const run of series.runs) {
      const segs = segments(run.map((p) => {
        const v = c.pick(p);
        return { at: xOf(p) / xMax, v: v == null ? v : Math.min(scale.hi, Math.max(scale.lo, v)) };
      }));
      for (const seg of segs) {
        if (seg.length < 2) continue;
        if (c.id === 'hr' && zones) {
          for (const r of runsBy(seg, ([, v]) => zoneColor(v))) lines.push({ color: r.key, points: polyline(r.items, lo, hi) });
        } else {
          lines.push({ color: c.color, points: polyline(seg, lo, hi) });
        }
      }
    }
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    return { chart: c, scale, lo, hi, lines, avg, min, max };
  }));

  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let shown = $derived(hover ?? at);
  let point = $derived(shown != null ? pts[shown] : null);

  $effect(() => {
    onscrub(point);
  });

  // xTicks mark each kilometer or mile, or round minutes for a timer, at
  // wider steps when there would be more than 10.
  let xTicks = $derived(byTime
    ? linearTicks(xMax, 8, [60, 120, 300, 600, 900, 1800, 3600, 7200]).map((s) => ({ at: s / xMax, text: s ? durationLabel(s) : '0' }))
    : linearTicks(xMax / unit, 10, [1, 2, 5, 10, 20, 50, 100]).map((k) => ({ at: (k * unit) / xMax, text: k ? (miles ? t('activitiesDistanceMi', { n: k }) : t('activitiesDistanceKm', { n: k })) : '0' })));

  // splitOf is the whole kilometer or mile a point falls in.
  const splitOf = (p: SeriesPoint) => Math.floor(p.d / unit);

  // stepSplit moves the guide to the start of the next or previous split.
  function stepSplit(dir: 1 | -1) {
    const cur = shown != null ? splitOf(pts[shown]) : dir > 0 ? -1 : splitOf(pts[pts.length - 1]) + 1;
    const want = cur + dir;
    const i = pts.findIndex((p) => splitOf(p) === want);
    if (i >= 0) {
      hover = null;
      at = i;
    }
  }

  function key(e: KeyboardEvent): boolean {
    if (byTime || (e.key !== 'PageDown' && e.key !== 'PageUp')) return false;
    stepSplit(e.key === 'PageDown' ? 1 : -1);
    return true;
  }

  // where says where the guide is: "2.10 km, 11:48" or a timer's "11:48".
  let where = $derived(point ? (byTime ? clockTime(point.t) : t('activitiesChartsWhere', { distance: length(point.d), time: clockTime(point.t) })) : '');
  // splitText is the averages of the split the guide is in: "Kilometer 3:
  // pace 5:12 /km, heart rate 150 bpm".
  let splitText = $derived.by(() => {
    if (!point || byTime) return '';
    const n = splitOf(point);
    const inSplit = pts.filter((p) => splitOf(p) === n);
    const parts = drawn.map((d) => {
      const vals = inSplit.map(d.chart.pick).filter((v): v is number => v != null);
      return vals.length ? t('activitiesChartsSplitValue', { chart: d.chart.kind, value: d.chart.format(vals.reduce((a, b) => a + b, 0) / vals.length) }) : '';
    }).filter(Boolean);
    return miles ? t('activitiesChartsSplitMile', { n: n + 1, values: parts.join(', ') }) : t('activitiesChartsSplitKilometer', { n: n + 1, values: parts.join(', ') });
  });
  // valueText is what a screen reader hears at the guide: where, each
  // chart's value, then the split's averages.
  let valueText = $derived(point
    ? [where, ...drawn.map((d) => { const v = d.chart.pick(point!); return v != null ? t('activitiesChartsValue', { chart: d.chart.kind, value: d.chart.format(v) }) : ''; }), splitText].filter(Boolean).join(', ')
    : byTime ? t('activitiesChartsNotPlaced') : t('activitiesChartsNotPlacedSplits'));
  let zoneTotal = $derived(zones ? zones.seconds.reduce((a, b) => a + b, 0) : 0);

  // readValue is a chart's value at the guide, or its average, with heart
  // rate's zone in words.
  function readValue(d: Drawn): string {
    const v = point ? d.chart.pick(point) : d.avg;
    if (v == null) return '–';
    if (d.chart.id !== 'hr' || !zones) return d.chart.format(v);
    const z = hrZone(v, zones.max_hr);
    return z < 0 ? t('activitiesChartsBelowZones', { value: d.chart.format(v) }) : t('activitiesChartsInZone', { value: d.chart.format(v), zone: z + 1 });
  }

  function readColor(d: Drawn): string {
    if (d.chart.id !== 'hr' || !zones) return d.chart.color;
    const v = point ? d.chart.pick(point) : d.avg;
    return v == null ? BELOW_ZONES : zoneColor(v);
  }
</script>

{#snippet zoneBars(z: Zones)}
  <div class="space-y-1">
    <p class="flex flex-wrap justify-between gap-2 text-sm">
      <span class="font-medium">{t('activitiesChartsZones')}</span>
      <span class="text-text-muted">{z.estimated ? t('activitiesChartsMaxHrEstimated', { hr: z.max_hr }) : t('activitiesChartsMaxHr', { hr: z.max_hr })}</span>
    </p>
    <ul class="space-y-1">
      {#each z.seconds as s, i (i)}
        {@const share = zoneTotal > 0 ? s / zoneTotal : 0}
        <li class="flex items-center gap-2 text-xs">
          <span class="w-32 shrink-0">{t('activitiesChartsZone', { zone: i + 1 })} <span class="text-text-muted">{t('activitiesChartsZoneRange', { lo: 50 + 10 * i, hi: 60 + 10 * i })}</span></span>
          <span class="flex-1 h-3 rounded-sm" style="background: var(--theme-surface1);">
            <span class="block h-full rounded-sm" style="width: {share * 100}%; background: {ZONE_COLORS[i]};"></span>
          </span>
          <span class="w-24 shrink-0 text-right">{t('activitiesChartsZoneTime', { minutes: Math.round(s / 60), percent: Math.round(share * 100) })}</span>
        </li>
      {/each}
    </ul>
  </div>
{/snippet}

{#if drawn.length}
  <section class="space-y-3" aria-label={t('activitiesCharts')}>
    <Readout title={point ? t('activitiesChartsAt', { where }) : t('activitiesChartsAverages')} items={drawn.map((d) => ({ label: d.chart.title, value: readValue(d), color: readColor(d) }))}
      hint={point ? splitText : byTime ? t('activitiesChartsHintTime') : t('activitiesChartsHintDistance')} />
    {#each drawn as d, k (d.chart.id)}
      {@const v = point ? d.chart.pick(point) : undefined}
      <div>
        <p class="text-sm mb-1">{d.chart.title}{#if d.chart.id === 'hr' && zones}<span class="text-xs text-text-muted ml-1.5">{t('activitiesChartsByZone')}</span>{/if}</p>
        <p class="sr-only">{t('activitiesChartsSummary', { chart: d.chart.kind, min: d.chart.format(d.min), avg: d.chart.format(d.avg), max: d.chart.format(d.max) })}</p>
        <Chart lo={d.lo} hi={d.hi} unit={d.chart.unit} yTicks={d.scale.ticks.map((tick) => ({ v: tick, text: d.chart.tick(tick) }))}
          {xTicks} xLabels={k === drawn.length - 1} height="h-28" {positions} bind:at bind:hover step={Math.max(1, Math.round(pts.length / 100))}
          label={t('activitiesChartsLabel', { chart: d.chart.kind, by: byTime ? 'time' : 'distance' })} {valueText} onkey={key}
          dots={v != null ? [{ v: Math.min(d.scale.hi, Math.max(d.scale.lo, v)), color: readColor(d) }] : []}>
          {#snippet plot()}
            {#each d.lines as l, i (i)}
              <polyline points={l.points} fill="none" stroke={l.color} stroke-width="2" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke" />
            {/each}
          {/snippet}
        </Chart>
        {#if d.chart.id === 'hr' && zones}
          <div class="mt-3">{@render zoneBars(zones)}</div>
        {/if}
      </div>
    {/each}
  </section>
{:else if zones}
  {@render zoneBars(zones)}
{/if}
